/// <reference path="../../../shared/type/auth-verify.d.ts" />
import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  REGEX_EMAIL,
  REGEX_PASSWORD_ALLSPECIAL_CHARACTER,
  REGEX_TEXT,
} from 'sources/apps/front-mgr/src/app/core/constants/constants';
import { InputComponent } from '../../../shared/ui/input/input.component';
import { SharedService } from '../../../shared/services/shared.service';
import { SelectData } from '../../../shared/type/input';
import { CardHeaderTitleSubtitleComponent } from '../../../shared/ui/card-header-title-subtitle/card-header-title-subtitle.component';
import { ModalService } from '../../../shared/services/modal.service';
import { Subscription } from 'rxjs';
import { AuthVerificationHandlerComponent } from '../../../shared/components/auth-verification-handler/auth-verification-handler.component';
import { SpinnerService } from '../../../../core/services/spinner.service';
import { RoutePaths } from '../../../../core/constants/route-paths';
import { Router } from '@angular/router';
import { AuthenticationService } from '../../services/authentication.service';
import {
  RegistrationUserData,
  RegistrerResponse,
} from '../../type/registration';
import { MustMatch } from '../../../shared/validators/must-match.validator';
import { RecaptchaService } from '../../../shared/services/recaptch.service';
import { DataSessionService } from '../../../../core/services/data-session.service';
import { DataLayerService } from '../../../../core/services/data-layer.service';

@Component({
  selector: 'registration-form-simple',
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    TranslateModule,
    ReactiveFormsModule,
    InputComponent,
    CardHeaderTitleSubtitleComponent,
    AuthVerificationHandlerComponent,
  ],
  templateUrl: './registration-form-simple.component.html',
  styleUrls: ['./registration-form-simple.component.scss'],
})
export class RegistrationFormSimpleComponent implements OnInit, OnDestroy {
  userData!: RegistrationUserData;
  errorGotten: boolean = false;
  internalUser: boolean = false;
  responseStatus!: AuthVerifMapKeys;
  endRegisterProcess: boolean = false;
  screenName = 'registration';

  countryList!: SelectData[];
  activityList!: SelectData[];
  langList!: SelectData[];

  registrationForm!: FormGroup;
  identificationNumberPattern!: RegExp;
  private subscription: Subscription = new Subscription();

  langSelected!: string;
  private langSubscription!: Subscription;

  showPassword = false;
  passwordType: 'passwordText' | 'password' = 'password';
  showValPassword = false;
  passwordValType: 'passwordText' | 'password' = 'password';

  constructor(
    private readonly _formBuilder: FormBuilder,
    private readonly _sharedService: SharedService,
    private readonly _authenticationService: AuthenticationService,
    private readonly _modalService: ModalService,
    private readonly _spinnerService: SpinnerService,
    private readonly _recaptchaService: RecaptchaService,
    private readonly _router: Router,
    private translateService: TranslateService,
    private readonly _dataSessionService: DataSessionService,
    private readonly _dataLayerService: DataLayerService
  ) {}

  ngOnInit(): void {
    this._dataLayerService.pushEventPageView(this.screenName);

    this.langSelected =
      this.translateService.currentLang || this.translateService.defaultLang;

    this.langSubscription = this.translateService.onLangChange.subscribe(
      (event) => {
        this.langSelected = event.lang;
      }
    );

    this.registrationForm = this._formBuilder.group(
      {
        firstName: ['', [Validators.required, Validators.pattern(REGEX_TEXT)]],
        lastName: ['', [Validators.required, Validators.pattern(REGEX_TEXT)]],
        secondLastName: ['', [Validators.pattern(REGEX_TEXT)]],
        country: [undefined, Validators.required],
        company: ['', [Validators.required, Validators.pattern(REGEX_TEXT)]],
        email: ['', [Validators.required, Validators.pattern(REGEX_EMAIL)]],
        validation_email: ['', [Validators.required]],
        password: [
          null,
          [
            Validators.required,
            Validators.pattern(REGEX_PASSWORD_ALLSPECIAL_CHARACTER),
          ],
        ],
        validation_password: ['', [Validators.required]],
        language: [undefined, Validators.required],
        activityType: ['', [Validators.required]],
        comments: ['', []],
        privacyPolicyAccepted: [false, this.isChecked],
      },
      {
        validator: [
          MustMatch('password', 'validation_password'),
          MustMatch('email', 'validation_email'),
        ],
      }
    );

    this._initValues();

    this.registrationForm
      .get('password')
      ?.valueChanges.subscribe((password: string) => {
        if (!password) {
          this.showPassword = false;
          this.passwordType = 'password';
        }
      });

    this.registrationForm
      .get('validation_password')
      ?.valueChanges.subscribe((password: string) => {
        if (!password) {
          this.showValPassword = false;
          this.passwordValType = 'password';
        }
      });
  }

  _initValues(): void {
    this.langList = this._sharedService.langData;
    this.activityList = this._sharedService.activityData;
    this.countryList =
      this.langSelected !== 'es'
        ? this._sharedService.countryData_en
        : this._sharedService.countryData_es;
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
    this.passwordType = this.showPassword ? 'passwordText' : 'password';
  }

  toggleValPasswordVisibility(): void {
    this.showValPassword = !this.showValPassword;
    this.passwordValType = this.showValPassword ? 'passwordText' : 'password';
  }

  async submitRegistrationForm(): Promise<void> {
    this._dataLayerService.pushEventClickButton(
      this.screenName,
      'continue',
      'registration-form-simple.continue'
    );
    const {
      firstName,
      lastName,
      secondLastName,
      country,
      company,
      email,
      password,
      language,
      activityType,
      comments,
      privacyPolicyAccepted,
    }: RegistrationUserData = this.registrationForm.value;

    this.userData = {
      firstName,
      lastName,
      secondLastName,
      country,
      company,
      email,
      password,
      language,
      activityType,
      comments,
      privacyPolicyAccepted,
    };
    this._spinnerService.show();
    this.internalUser = this._authenticationService.isInternalUserDomain(
      this.userData.email
    );
    try {
      const token = await this._recaptchaService.reCAPTCHA('REGISTER');

      if (!this.internalUser) {
        const sub = this._authenticationService
          .postRegister(this.userData)
          .subscribe({
            next: (res) => {
              const response: RegistrerResponse = res;
              this.responseStatus =
                this._authenticationService.getRegisterStatus(response.state);
              this._dataLayerService.pushEventSubmit(
                this.screenName,
                'register',
                undefined,
                'register'
              );
            },
            error: (err) => {
              if (err.error?.state === 'EXC07') {
                this._dataSessionService.showModalRecaptchaError(() =>
                  this.reloadForm()
                );
              } else {
                this.responseStatus = 'registerError';
              }
              this._dataLayerService.pushEventSubmitError(
                this.screenName,
                'register',
                err.error?.state ?? err.status,
                'Error trying to register an external user'
              );
              this._spinnerService.hide();
              this.endRegisterProcess = true;
            },
            complete: () => {
              this._spinnerService.hide();
              this.endRegisterProcess = true;
              this._dataLayerService.pushEventSubmitError(
                this.screenName,
                'register',
                'internal_user',
                'User is trying to register an internal user'
              );
            },
          });

        this.subscription.add(sub);
      } else {
        this._spinnerService.hide();
        this.endRegisterProcess = true;
      }
    } catch (error) {
      this._spinnerService.hide();
      this._dataLayerService.pushEventSubmitError(
        this.screenName,
        'recaptcha_error',
        'register',
        'Error executing reCAPTCHA from registration form'
      );
      console.error('Error executing reCAPTCHA', error);
    }
  }

  getCountry(value: string): string {
    const country = this.countryList.find((item) => item.value === value);
    return country ? country.label : '';
  }

  async openModal() {
    this._dataLayerService.pushEventClickLink(
      this.screenName,
      'privacy_policy',
      'registration-form-simple.privacy_check5'
    );
    this._modalService.openPolicityCookieModal();
  }

  goBackFromVerification(e: boolean): void {
    this._dataLayerService.pushEventPageView(this.screenName);
    this.endRegisterProcess = false;
  }

  goLogin(e: boolean): void {
    this._dataLayerService.pushEventClickLink(
      this.screenName,
      'cancel',
      'registration-form-simple.cancel'
    );
    this._router.navigate([`${RoutePaths.login}`]);
  }

  isChecked(control: FormControl): any {
    if (control.value !== true) {
      return {
        notChecked: true,
      };
    }
    return undefined;
  }

  reloadForm(): void {
    this.registrationForm.reset();
    this.errorGotten = false;
    this._spinnerService.hide();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.langSubscription.unsubscribe();
  }
}
